import { colors } from '@lmc/common/constants'
import { Icon, Popover } from '@lmc/ui-kit'
import dayjs from 'dayjs'
import type { CSSProperties } from 'react'
import styled from 'styled-components'
import { isLowerThen564px } from '../../common/utils'

interface CarePlanNextReviewDateProps {
  alert?: {
    text?: string
    type?: string
  }
  date?: string
  noFurtherSupportRequired?: boolean
  style?: CSSProperties
}

interface ReviewDateResult {
  result: string
  isOverdue: boolean
}

function getReviewDate(date: string): ReviewDateResult {
  let result = `Due ${dayjs(date).format('DD-MM-YYYY')}`
  let isOverdue = false

  const reviewDate = dayjs(date).startOf('day')
  const today = dayjs().startOf('day')

  if (reviewDate.isSame(today)) {
    result = 'Today'
  } else if (reviewDate.isAfter(today)) {
    if (dayjs(reviewDate).diff(today, 'days') === 1) {
      result = 'Tomorrow'
    }
  } else {
    isOverdue = true
    const overdueDays = dayjs(reviewDate).diff(today, 'days')

    if (overdueDays === -1) {
      result = 'Overdue +1 day'
    } else {
      result = `Overdue +${Math.abs(overdueDays)} days`
    }
  }

  return { result, isOverdue }
}

function CarePlanNextReviewDate({
  alert,
  date,
  noFurtherSupportRequired,
  style
}: CarePlanNextReviewDateProps) {
  if (!date) return null
  const { result, isOverdue } = getReviewDate(date)
  const isAlert = !!(alert || isOverdue)

  const alertDate =
    alert && alert.type === 'updated' && !isOverdue ? 'Due now' : result

  if (noFurtherSupportRequired) return null
  return (
    <Wrapper style={style}>
      <ReviewText $isOverdue={isOverdue}>
        {alert ? (
          isLowerThen564px ? (
            <Popover
              placement="bottomRight"
              content={<PopupText>{alert.text}</PopupText>}
            >
              {isLowerThen564px ? alertDate.slice(0, 7) : alertDate}
            </Popover>
          ) : (
            <span>
              {alertDate}
              <br />
              <AlertText>{alert.text}</AlertText>
            </span>
          )
        ) : isLowerThen564px ? (
          result.slice(0, 7)
        ) : (
          result
        )}
      </ReviewText>
      <ReviewDateIcon
        $isAlert={isAlert}
        name={isAlert ? 'ExclamationCircleOutlined' : 'CheckCircleOutlined'}
      />
    </Wrapper>
  )
}

const AlertText = styled.span`
  color: ${colors.darkgrey};
`

const PopupText = styled.span`
  background: ${colors.black};
  border-radius: 5px;
  color: ${colors.white};
  padding: 5px;
`

const ReviewDateIcon = styled(Icon)<{ $isAlert: boolean }>`
  align-self: center;
  color: ${({ $isAlert }) => ($isAlert ? colors.red : colors.successgreen)};
  font-size: 18px;
  margin-left: 12px;
`

const ReviewText = styled.span<{ $isOverdue: boolean }>`
  display: block;
  align-self: center;
  color: ${({ $isOverdue }) => ($isOverdue ? colors.red : colors.black)};
  text-align: right;
  white-space: nowrap;
`

const Wrapper = styled.div`
  display: flex;
`

export default CarePlanNextReviewDate
